import { CssBaseline, createTheme, ThemeProvider } from '@mui/material';
import { ApolloProvider, createApolloClient, fetchAuthSession } from '@parkchirp/api-client';
import {
	getOrgColorTheme,
	type LogoProps,
	MainContainer,
	type NavComponentProps,
	type OrgColorTheme,
	Sidebar,
} from '@parkchirp/ui-library';
import { DuplicateActionGuard } from 'common-ui-library/src/components/duplicateActionGuard';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { AccessControlledComponents } from '~/constants';
import useAppContext from '~/hooks/useAppContext';
import { GET_DASHBOARD_ACCESS_CONFIG, LIST_ORGANIZATIONS } from '~/queries';

import AppHead from './components/AppHead';
import Header from './components/Header';
import Loading from './components/Loading';
import Logo from './components/Logo';
import SidebarNav from './components/Sidebar/SidebarNav';
import NavLinks from './components/Sidebar/SidebarNavlinks';

type Props = {
	children?: ReactElement;
};

const SmallLogo = ({ theme }: { theme: OrgColorTheme }) => (
	<Logo src={theme.logo.small.src} height={theme.logo.small.height} width={theme.logo.small.width} />
);

const LargeLogo = ({ theme }: { theme: OrgColorTheme }) => (
	<Logo src={theme.logo.large.src} height={theme.logo.large.height} width={theme.logo.large.width} />
);

const SmallLogoComponent = (props: LogoProps) => <SmallLogo {...props} />;
const LargeLogoComponent = (props: LogoProps) => <LargeLogo {...props} />;

const AppLayout: FC<Props> = ({ children }) => {
	const {
		initialized,
		setInitialized,
		setShowDialog,
		showDialog,
		showSnackbar,
		setShowSnackbar,
		setDashboardAccessConfig,
		setDashboardAccessConfigLoading,
		setOrganizationList,
		hasComponentAccess,
		headerDetails,
		dashboardAccessConfigLoading,
	} = useAppContext();

	const [cookies] = useCookies();
	const [orgName, setOrgName] = useState('parkchirp');
	const client = useMemo(() => createApolloClient({ cookies }, false), [cookies]);
	const pathname = usePathname().slice(1);
	const router = useRouter();
	const normalizedPathname = pathname.replace(/\/$/, '');
	const hasControlPanelAccess = hasComponentAccess(AccessControlledComponents.DashboardPage);
	const hideDashboard = !hasComponentAccess(AccessControlledComponents.AdminControlTool);
	const isMetricsPage = normalizedPathname === 'metrics';
	const isControlPanelPage = normalizedPathname === 'control-panel';
	const waitingForDashboardAccess = dashboardAccessConfigLoading;
	const shouldHideLandingPage =
		(isMetricsPage || isControlPanelPage) &&
		(waitingForDashboardAccess ||
			(isMetricsPage && hideDashboard) ||
			(isControlPanelPage && !hasControlPanelAccess));

	useEffect(() => {
		if (waitingForDashboardAccess || router.query.code) {
			return;
		}

		if (isMetricsPage && hideDashboard) {
			router.replace(hasControlPanelAccess ? '/control-panel' : '/subscriptions');
			return;
		}

		if (isControlPanelPage && !hasControlPanelAccess) {
			router.replace('/subscriptions');
		}
	}, [hasControlPanelAccess, hideDashboard, isControlPanelPage, isMetricsPage, waitingForDashboardAccess, router]);

	const HeaderDetailsWrapper = (props: any) => <Header {...props} headerDetails={headerDetails} />;

	useEffect(() => {
		if (!initialized) setInitialized(true);
	}, [initialized, setInitialized]);

	useEffect(() => {
		const fetchDashboardAccessConfig = async () => {
			if (client) {
				try {
					const { data: dashboardAccessConfig } = await client.query({
						query: GET_DASHBOARD_ACCESS_CONFIG,
						context: {
							apiVersion: 'v2',
						},
					});
					setDashboardAccessConfig(dashboardAccessConfig.getDashboardAccessConfig);
				} catch (err) {
					console.log(err);
				} finally {
					setDashboardAccessConfigLoading(false);
				}
			}
		};

		fetchDashboardAccessConfig();
	}, [client, setDashboardAccessConfig, setDashboardAccessConfigLoading]);

	useEffect(() => {
		const fetchOrganizations = async () => {
			if (client) {
				const { data } = await client.query({
					query: LIST_ORGANIZATIONS,
					context: {
						apiVersion: 'v2',
					},
				});
				const orgs = data.listOrganizations
					.map((org) => ({ id: org.organizationId, name: org.name }))
					.filter((org) => org.name);
				setOrganizationList(orgs);
				if (orgs.length === 1) {
					setOrgName(orgs[0].name);
				} else {
					// only parkchirp admin should have multiple
					setOrgName('parkchirp');
				}
			}
		};
		fetchOrganizations();
	}, [client, setOrganizationList]);

	const theme = useMemo(() => createTheme(getOrgColorTheme(orgName)), [orgName]);

	const filteredNavLinksOnAccessLevel = useCallback(
		() =>
			NavLinks.filter(
				({ path, accessControlComponentName }) =>
					!(path === 'metrics' && (hideDashboard || waitingForDashboardAccess)) &&
					(!accessControlComponentName || hasComponentAccess(accessControlComponentName)),
			),
		[hasComponentAccess, hideDashboard, waitingForDashboardAccess],
	);

	const NavComponentWrapper = (props: NavComponentProps) => (
		<SidebarNav navLinks={filteredNavLinksOnAccessLevel()} {...props} />
	);

	const titleMap: { [key: string]: string } = {
		metrics: 'Dashboard',
		'metrics/': 'Dashboard',
		'control-panel': 'Control Panel',
		'control-panel/': 'Control Panel',
		'draft-invoice/manual/': 'Monthly Invoice Draft',
		'draft-invoice/carryover/': 'Carryover Invoice Draft',
		'draft-invoice/miscellaneous/': 'Miscellaneous Invoice Draft',
		'draft-invoice/hotel-storage/': 'Hotel Storage Invoice Draft',
		'draft-invoice/office-retail-rent/': 'Office Retail Rent Invoice Draft',
		'draft-invoice/validation/': 'Validation Invoice Draft',
		'manual-invoice/monthly/': 'Manual Monthly Invoice',
		'manual-invoice/carryover/': 'Manual Carryover Invoice',
		'manual-invoice/miscellaneous/': 'Manual Miscellaneous Invoice',
		'manual-invoice/office-retail-rent/': 'Manual Office Retail Rent Invoice',
		'manual-invoice/validation/': 'Manual Validation Invoice',
		'manual-invoice/hotel-storage/': 'Manual Hotel Storage Invoice',
		'bulkEmail/': 'Bulk Email',
		'lease/': 'Create Lease',
		'lease-detail/': 'Lease Detail',
		'payments/achRejections/': 'ACH Rejections',
		'portals/create/': 'Create Hourly Portal Account',
		'portals/users/': 'Portals',
		'portals/usergroups/': 'Portals',
		'scan-to-pay/': 'Scan To Pay',
		'scan-to-pay/accounting/': 'Scan To Pay',
		'scan-to-pay/sessions/': 'Scan To Pay',
		'scan-to-pay/sessions/expired/': 'Scan To Pay',
		'scan-to-pay/sessions/unclaimed/': 'Scan To Pay',
		'scan-to-pay/sessions/validated/': 'Scan To Pay',
		'scan-to-pay/zones/': 'Scan To Pay',
		'scan-to-pay/zones/edit/': 'Scan To Pay',
		'scan-to-pay/zones/create/': 'Scan To Pay',
		'scan-to-pay/fees/': 'Scan To Pay',
		'scan-to-pay/rates/': 'Scan To Pay',
		'scan-to-pay/rates/events': 'Scan To Pay',
		'scan-to-pay/rates/overlay/': 'Scan To Pay',
		'scan-to-pay/rates/tiers/': 'Scan To Pay',
		'scan-to-pay/validations/': 'Scan To Pay',
		'scan-to-pay/validations/edit/': 'Scan To Pay',
		'scan-to-pay/validations/create/': 'Scan To Pay',
		'reservations/': 'Reservations',
		'reservations/accounting/': 'Reservations',
		'reservations/sessions/': 'Reservations',
		'reservations/sessions/active/': 'Reservations',
		'reservations/sessions/all/': 'Reservations',
		'reservations/sessions/expired/': 'Reservations',
		'reservations/zones/': 'Reservations',
		'reservations/zones/edit/': 'Reservations',
		'reservations/zones/create/': 'Reservations',
		'reservations/fees/': 'Reservations',
		'reservations/fees/transaction-fees/': 'Reservations',
		'reservations/fees/vehicle-surcharge-fees/': 'Reservations',
		'reservations/rates/': 'Reservations',
		'reservations/rates/overlay/': 'Reservations',
		'reservations/rates/event/': 'Reservations',
		'reservations/rates/tiers/': 'Reservations',
		'reservations/validations/': 'Reservations',
		'reservations/validations/edit/': 'Reservations',
		'reservations/validations/create/': 'Reservations',
		'reservations/access-system-history/': 'Reservations',
	};

	// Normalize and determine the title
	const pathSplit = pathname.split('/');
	let title =
		pathSplit.length > 1 && pathSplit[1]
			? pathname.split('/').slice(1).join(' ').replace(/-/g, ' ')
			: pathname.split('/').slice(0).join(' ').replace(/-/g, ' ');
	if (titleMap[pathname]) {
		title = titleMap[pathname];
	}

	return (
		<ThemeProvider theme={theme}>
			<AppHead theme={theme} orgName={orgName} appName='Admin Dashboard' />
			<CssBaseline />
			<DuplicateActionGuard />
			<div id='app'>
				<Sidebar
					theme={theme as any}
					SmallLogo={SmallLogoComponent}
					LargeLogo={LargeLogoComponent}
					NavComponent={NavComponentWrapper}
				/>
				{client ? (
					<ApolloProvider client={client}>
						<MainContainer
							HeaderDetails={HeaderDetailsWrapper}
							headerDetails={headerDetails}
							headerTitle={title}
							navLinks={filteredNavLinksOnAccessLevel()}
							showDialog={showDialog}
							setShowDialog={setShowDialog}
							showSnackbar={showSnackbar}
							setShowSnackbar={setShowSnackbar}
							error=''
							pathname={pathname}
						>
							{shouldHideLandingPage ? <Loading /> : children}
						</MainContainer>
					</ApolloProvider>
				) : (
					<Loading />
				)}
			</div>
		</ThemeProvider>
	);
};

export default AppLayout;
